/**
 * Carbon Compactors — Attribution capture
 *
 * Captures Google Ads / Microsoft Ads click IDs + UTM params from the URL
 * on ANY page, stores them in first-party cookies for 2 years, and attaches
 * them to:
 *   1. Contact form hidden fields (read by cc-contact.liquid)
 *   2. Shopify cart attributes (so orders carry the original click ID through
 *      checkout even if the customer doesn't log in)
 *
 * Cookie naming: _cc_attr_<param> — e.g. _cc_attr_gclid
 *
 * Philosophy: first-touch wins. Once a cookie is set, it's not overwritten
 * unless the user returns via a newer, different ad click. This preserves the
 * original click even if they come back direct/organic before buying.
 */
(function () {
  'use strict';

  var COOKIE_PREFIX = '_cc_attr_';
  var COOKIE_MAX_AGE = 60 * 60 * 24 * 365 * 2; // 2 years
  var TRACKED_PARAMS = [
    'gclid',          // Google Ads click ID
    'gbraid',         // Google Ads iOS 14+ app attribution
    'wbraid',         // Google Ads web-to-app attribution
    'fbclid',         // Meta (Facebook/Instagram) click ID
    'msclkid',        // Microsoft Ads click ID
    'gad_source',     // Google Ads surface that served the click
    'gad_campaignid', // Google Ads campaign ID on the click
    'srsltid',        // Google appends this to Merchant Center listing clicks AND
                      // to ordinary organic search results. It does NOT mean
                      // "came from Shopping" — most srsltid traffic is plain
                      // organic search. Use mc_feed below to identify real feed
                      // clicks; bare + srsltid means organic search.
    'mc_feed',        // OUR tag: which Merchant Center feed served the click
                      // (autofeed | us | intl_usd | eur | ...)
    'mc_item',        // OUR tag: which offer/variant was clicked — makes the
                      // per-colour feed-title test readable
    // gp_ = google paid. Set as the account/campaign final_url_suffix, so every
    // paid click self-describes. gp_kw is the keyword WE BID ON that matched —
    // NOT what the user typed. Google exposes no ValueTrack for the search term.
    'gp_kw',          // {keyword}     — matched keyword (Search)
    'gp_mt',          // {matchtype}   — e / p / b
    'gp_cid',         // {campaignid}
    'gp_agid',        // {adgroupid}
    'gp_dev',         // {device}      — m / t / c
    'gp_ad',          // {creative}    — the AD ID. Google exposes no ValueTrack for
                      // which headline combination rendered, so this is the finest
                      // per-click granularity available: which ad, not which copy.
    'gp_pid',         // {product_id}  — Shopping only
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'referrer_domain',
  ];

  // A new one of these means the ad platform is explicitly re-claiming this
  // visitor, so it overwrites a stored value. Everything else is first-touch.
  var CLICK_ID_PARAMS = ['gclid', 'gbraid', 'wbraid', 'fbclid', 'msclkid'];

  // These ride along with a Google Ads click. They must refresh together with
  // the click ID, or we'd pair a fresh gclid with a stale campaign.
  // srsltid is deliberately NOT here: it is not an ad click, so it stays
  // first-touch like the UTMs.
  var CLICK_CONTEXT_PARAMS = ['gad_source', 'gad_campaignid'];

  function getCookie(name) {
    var match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? decodeURIComponent(match[2]) : null;
  }

  function setCookie(name, value, maxAgeSeconds) {
    var isSecure = window.location.protocol === 'https:';
    document.cookie = name + '=' + encodeURIComponent(value) +
      '; path=/' +
      '; max-age=' + maxAgeSeconds +
      '; SameSite=Lax' +
      (isSecure ? '; Secure' : '');
  }

  function getQueryParam(name) {
    var params = new URLSearchParams(window.location.search);
    return params.get(name);
  }

  /**
   * Capture params from the URL. First-touch attribution: if the cookie is
   * already set, we keep it UNLESS this visit has a fresher gclid/gbraid/etc.
   * (i.e., the ad platform explicitly claims this click).
   */
  function capture() {
    var freshClickId = CLICK_ID_PARAMS.some(function (p) {
      return !!getQueryParam(p);
    });

    TRACKED_PARAMS.forEach(function (param) {
      var cookieName = COOKIE_PREFIX + param;
      var urlValue = getQueryParam(param);

      if (param === 'referrer_domain') {
        // Only set if not already set and there's a usable referrer
        if (!getCookie(cookieName) && document.referrer) {
          try {
            var ref = new URL(document.referrer);
            if (ref.hostname && ref.hostname !== window.location.hostname) {
              setCookie(cookieName, ref.hostname, COOKIE_MAX_AGE);
            }
          } catch (e) { /* noop */ }
        }
        return;
      }

      if (urlValue) {
        // Ad-platform click IDs OVERWRITE on a new click. UTM params only
        // set on first touch (preserves org-level attribution if user returns
        // via organic for a second visit).
        var isClickId = CLICK_ID_PARAMS.indexOf(param) !== -1;
        var isClickContext = freshClickId &&
                             CLICK_CONTEXT_PARAMS.indexOf(param) !== -1;
        if (isClickId || isClickContext || !getCookie(cookieName)) {
          setCookie(cookieName, urlValue, COOKIE_MAX_AGE);
        }
      }
    });

    // Record first-touch timestamp + original landing page URL
    if (!getCookie(COOKIE_PREFIX + 'first_seen')) {
      setCookie(COOKIE_PREFIX + 'first_seen', new Date().toISOString(), COOKIE_MAX_AGE);
      setCookie(COOKIE_PREFIX + 'landing_page', window.location.pathname + window.location.search, COOKIE_MAX_AGE);
    }

    // If any ad click ID was on this visit, record a last-touch timestamp too
    if (freshClickId) {
      setCookie(COOKIE_PREFIX + 'last_click', new Date().toISOString(), COOKIE_MAX_AGE);
    }
  }

  /**
   * Populate any hidden form inputs named like name="contact[<param>]"
   * with the cookie value for that param. Runs on DOM ready AND on each form
   * submit (in case cookies were set after initial render).
   */
  function populateFormFields() {
    var fields = document.querySelectorAll('input[data-cc-attribution]');
    fields.forEach(function (input) {
      var param = input.getAttribute('data-cc-attribution');
      var value = getCookie(COOKIE_PREFIX + param);
      if (value) input.value = value;
    });
  }

  /**
   * Write attribution to the Shopify cart as note attributes. Runs ONCE per
   * browser session (deduped via sessionStorage) — no point hitting the Cart
   * API on every page load since the attributes persist on the cart session
   * for the lifetime of the cart. First call plants the flag; subsequent
   * loads skip.
   *
   * When the customer adds a product + checks out, Shopify automatically
   * copies cart.note_attributes to the resulting order.note_attributes.
   */
  function writeCartAttributes() {
    if (!window.fetch) return;
    try {
      if (sessionStorage.getItem('_cc_cart_attr_written') === '1') return;
    } catch (e) { /* sessionStorage disabled — carry on */ }

    var attributes = {};
    TRACKED_PARAMS.concat(['first_seen', 'last_click', 'landing_page']).forEach(function (param) {
      var value = getCookie(COOKIE_PREFIX + param);
      if (value) attributes['_cc_' + param] = value;
    });
    if (Object.keys(attributes).length === 0) return;

    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ attributes: attributes }),
    }).then(function () {
      try { sessionStorage.setItem('_cc_cart_attr_written', '1'); } catch (e) { /* noop */ }
    }).catch(function () { /* noop — cart may not exist yet */ });
  }

  // Run capture immediately
  capture();

  // Populate forms + write cart attributes once DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      populateFormFields();
      writeCartAttributes();
    });
  } else {
    populateFormFields();
    writeCartAttributes();
  }

  // Re-populate form fields just before any form submit (belt-and-suspenders)
  document.addEventListener('submit', populateFormFields, true);

  // Expose a small API for debugging / manual integration
  window.CCAttribution = {
    get: function (param) { return getCookie(COOKIE_PREFIX + param); },
    getAll: function () {
      var all = {};
      TRACKED_PARAMS.concat(['first_seen', 'last_click', 'landing_page']).forEach(function (p) {
        var v = getCookie(COOKIE_PREFIX + p);
        if (v) all[p] = v;
      });
      return all;
    },
  };
})();
